/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	BlurredImage,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
	BackgroundContainer,
} from "@components/elements";
import { ResellerCardFragment, ResellersSearchSectionFragment } from "@cosmos/hygraph-schema";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";
import { useCallback, useMemo, useRef } from "react";
import { ResellersSearchMolecule } from "./ResellersSearch.molecule";

export type ResellersSearchSectionProps = ResellersSearchSectionFragment & {
	skipRouter?: boolean;
};

const descStyles = css({
	transform: "translateZ(0)",
	"& .title-desc-stack .cosmos-rich-text": { color: "eds.text.primary" },
});

const bgImageStyles = css({
	position: "absolute",
	pointerEvents: "none",
	top: 0,
	bottom: 0,
	right: 0,
	left: 0,
	width: "100%",
	aspectRatio: "16 / 9",
});

export const ResellersSearchSection = ({
	config,
	blurredBackgroundImage,
	cards,
	cards2,
	cards3,
	cards4,
	...rest
}: ResellersSearchSectionProps): React.JSX.Element | null => {
	const sectionRef = useRef<HTMLDivElement>(null);
	const scrollToSection = useCallback(() => {
		setTimeout(() => {
			sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	}, []);

	const combinedCards = useMemo(() => {
		const newArr: ResellerCardFragment[] = [];
		return newArr
			.concat(cards || [], cards2 || [], cards3 || [], cards4 || [])
			.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
	}, [cards, cards2, cards3, cards4]);
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(combinedCards?.length || rest?.noResultsLabel);
	if (!validHeading && !validContent) return null;

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgContain={true}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer ref={sectionRef} disablePaddingBlockEnd={!!config?.disablePaddingBottom}>
					<div className={bgImageStyles}>
						<BlurredImage
							image={blurredBackgroundImage}
							width={1920}
							height={1080}
							opacity={0.27}
							disableAutoplay
							blurPixels={150}
						/>
					</div>
					<VStack alignItems="stretch" gap="eds.dimension.96" className={cx("resellers-search-section", descStyles)}>
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<ResellersSearchMolecule {...rest} triggerScroll={scrollToSection} cards={combinedCards} />
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
